import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { IPromoInformation } from '../../../api/fetchers/promotions/types';
import { fetchAssignedTablesAction } from '../../apiActions/fetchAssignedTablesAction';
import {
  fetchPromoPointsAction,
  fetchPromotionAction,
  fetchPromotionTranslationAction,
} from '../../apiActions/fetchPromotionAction';

import { PromotionState, TopTournamentType, WinningCondition } from './types';

export const promotionSlice = createSlice({
  name: 'promotions',
  initialState: {
    isRedEnvelope: false,
    loading: true,
    showTrophyTooltip: false,
    wasTrophyTooltipShown: false,
    hasRoundLimitTrophyShown: false,
    prizeDrops: [],
    tournaments: [],
    cashbacks: [],
    activePromos: [],
    winPopup: null,
    translations: undefined,
    assignedTables: null,
    promoPoints: null,
    isRoundLimitExceed: false,
    topTournament: undefined,
  } as PromotionState,
  reducers: {
    setWinPopup: (state, action: PayloadAction<IPromoInformation | null>) => {
      state.winPopup = action.payload;
    },
    changeShowTrophyTooltip: (state, action: PayloadAction<boolean>) => {
      state.showTrophyTooltip = action.payload;
      if (action.payload) {
        state.wasTrophyTooltipShown = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromotionAction.fulfilled, (state, action) => {
        state.prizeDrops = action.payload.pds;
        state.tournaments = action.payload.tms;
        state.cashbacks = action.payload.cbs;
        const currentDate = Date.now();
        state.activePromos = [...state.tournaments, ...state.prizeDrops, ...state.cashbacks]
          .filter((promo) => promo.cmode === 'D')
          .filter((promo) => promo.opt === 'opted' || promo.opt === 'no_pop' || (promo.opt === 'pop' && !promo.cnst))
          .filter((promo) => {
            const startDate = promo.sd * 1000;
            const endDate = promo.ed * 1000;
            return currentDate >= startDate && currentDate <= endDate;
          });
        state.loading = false;
      })
      .addCase(fetchPromotionAction.rejected, (state, action) => {
        state.error = action.error;
        state.loading = false;
      })
      .addCase(fetchPromotionAction.pending, (state) => {
        state.loading = true;
        state.error = void 0;
      })
      .addCase(fetchPromotionTranslationAction.fulfilled, (state, action) => {
        state.translations = action.payload;
      })
      .addCase(fetchAssignedTablesAction.fulfilled, (state, action) => {
        state.assignedTables = action.payload;
      })
      .addCase(fetchPromoPointsAction.fulfilled, (state, action) => {
        state.promoPoints = action.payload;
        const roundLimitExceededPromos: boolean[] = state.activePromos.map((promo) => {
          const promoId = promo.tid || promo.cbid;
          return promoId ? action.payload[promoId]?.isRoundLimitExceeded : false;
        });
        // Check if at least one irle value
        if (roundLimitExceededPromos.length > 0) {
          const isRoundLimitExceeded: boolean = roundLimitExceededPromos.every(Boolean);
          state.isRoundLimitExceed = isRoundLimitExceeded;
          if (isRoundLimitExceeded && !state.hasRoundLimitTrophyShown) {
            state.wasTrophyTooltipShown = false;
            state.hasRoundLimitTrophyShown = true;
          }
        }
        const currentDate = Date.now();
        const activeTournaments = [...state.tournaments]
          .filter((promo) => promo.cmode === 'D')
          .filter((promo) => promo.opt === 'opted' || promo.opt === 'no_pop' || (promo.opt === 'pop' && !promo.cnst))
          .filter((promo) => {
            const startDate = promo.sd * 1000;
            const endDate = promo.ed * 1000;
            return currentDate >= startDate && currentDate <= endDate;
          });
        if (activeTournaments) {
          let topTournament: TopTournamentType | undefined; // tournament with highest winStreak
          for (const tournament of activeTournaments) {
            const tournamentPoints = action.payload[tournament.tid];
            if (tournamentPoints) {
              const tournamentToCompare = {
                id: tournament.tid.toString(),
                winStreak: tournamentPoints.winStreak,
                lastWonScore: tournamentPoints.lastWonScore,
                lastGameId: tournamentPoints.lastGameId,
                winningCondition: tournament.TOURNAMENT_DETAILS ? tournament.TOURNAMENT_DETAILS.wc : '',
                endDate: tournament.ed,
                showWsBadge: tournamentPoints.sb,
              };
              if (topTournament) {
                if (
                  topTournament.winStreak < tournamentToCompare.winStreak ||
                  (topTournament.winStreak == tournamentToCompare.winStreak &&
                    tournamentToCompare.endDate < topTournament.endDate)
                ) {
                  topTournament = tournamentToCompare;
                }
              } else if (!topTournament) {
                topTournament = tournamentToCompare;
              }
            }
          }
          if (topTournament) {
            if (state.isRoundLimitExceed) {
              topTournament.showWsBadge = 0;
              return;
            }
            if (
              topTournament.winningCondition == WinningCondition.TotalBet ||
              topTournament.winningCondition == WinningCondition.TotalPoints ||
              topTournament.winningCondition == WinningCondition.Empty
            ) {
              topTournament.showWsBadge = 0;
            }
          }
          state.topTournament = topTournament;
        }
      });
  },
});

export const promotionActions = promotionSlice.actions;
